import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '../../components/common/UIComponents';
import {
  communityService,
  type CommunityModerationOutcomeAction,
  type OwnCommunityNotificationOutcome,
} from '../../services/communityService';
import { notificationService } from '../../services/notificationService';
import { getFirebaseRuntimeErrorCode } from '../../utils/firebaseRuntimeError';

type OutcomePageState =
  | { kind: 'loading' }
  | { kind: 'resolved'; outcome: OwnCommunityNotificationOutcome }
  | { kind: 'unavailable' }
  | { kind: 'error' };

const resolutionCopy: Record<'reviewed' | 'dismissed' | 'action_taken', string> = {
  reviewed: 'Twoje zgłoszenie zostało sprawdzone.',
  dismissed: 'Twoje zgłoszenie zostało sprawdzone i zamknięte.',
  action_taken: 'Twoje zgłoszenie zostało sprawdzone i podjęto działanie.',
};

const moderationCopy: Record<CommunityModerationOutcomeAction, string> = {
  post_hidden: 'Twój wpis w Społeczności został ukryty.',
  thread_hidden: 'Twój wątek w Społeczności został ukryty.',
  thread_restored: 'Twój wątek w Społeczności został przywrócony.',
  thread_closed: 'Twój wątek w Społeczności został zamknięty.',
  thread_reopened: 'Twój wątek w Społeczności został ponownie otwarty.',
};

const unavailableCodes = [
  'invalid-argument',
  'not-found',
  'permission-denied',
  'failed-precondition',
  'unauthenticated',
];

const isUnavailableOutcomeError = (error: unknown): boolean => {
  const code = getFirebaseRuntimeErrorCode(error);
  return unavailableCodes.some((candidate) => code.includes(candidate));
};

const formatResolvedAt = (resolvedAtMillis: number): string =>
  new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(new Date(resolvedAtMillis));

const OutcomeShell: React.FC<{ children: React.ReactNode; label?: string }> = ({
  children,
  label = 'Wynik zgłoszenia',
}) => (
  <div className="rs-community-page overflow-x-clip">
    <main
      className="rs-community-shell mx-auto max-w-3xl"
      aria-labelledby="community-outcome-title"
    >
      <nav aria-label="Okruszki" className="text-sm font-medium text-primary-light-400">
        <Link to="/app/community" className="transition-colors hover:text-primary-light-200">
          Społeczność
        </Link>
        <span aria-hidden="true" className="px-2">
          /
        </span>
        <span>{label}</span>
      </nav>
      {children}
    </main>
  </div>
);

export const CommunityOutcomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const notificationId = String(searchParams.get('notificationId') || '').trim();
  const [retryVersion, setRetryVersion] = useState(0);
  const [state, setState] = useState<OutcomePageState>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    if (!/^[A-Za-z0-9_-]{1,256}$/.test(notificationId)) {
      setState({ kind: 'unavailable' });
      return () => {
        active = false;
      };
    }

    setState({ kind: 'loading' });
    void communityService
      .getOwnNotificationOutcome(notificationId)
      .then(async (outcome) => {
        if (!active) return;
        setState({ kind: 'resolved', outcome });
        try {
          const marked = await notificationService.markCommunityOutcomeNotificationAsRead(
            outcome.kind === 'community_report_resolved'
              ? { notificationId, resolutionStatus: outcome.resolutionStatus }
              : { notificationId, moderationAction: outcome.moderationAction }
          );
          if (!marked) {
            console.warn('Nie udało się potwierdzić odczytania wyniku zgłoszenia.');
          }
        } catch (readbackError) {
          console.warn('Nie udało się oznaczyć wyniku zgłoszenia jako odczytanego.', readbackError);
        }
      })
      .catch((error) => {
        if (!active) return;
        setState({ kind: isUnavailableOutcomeError(error) ? 'unavailable' : 'error' });
      });

    return () => {
      active = false;
    };
  }, [notificationId, retryVersion]);

  if (state.kind === 'loading') {
    return (
      <OutcomeShell>
        <section className="rs-community-state p-6 text-center" aria-live="polite">
          <Spinner className="mx-auto" />
          <h1
            id="community-outcome-title"
            className="mt-4 text-xl font-semibold text-primary-light-100"
          >
            Sprawdzamy wynik zgłoszenia
          </h1>
          <p className="mt-2 text-sm text-primary-light-300">
            Potwierdzamy bezpiecznie właściwe zgłoszenie i jego wynik.
          </p>
        </section>
      </OutcomeShell>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <OutcomeShell>
        <section className="rs-community-state p-5 sm:p-6">
          <InformationCircleIcon className="h-9 w-9 text-primary-light-300" aria-hidden="true" />
          <h1
            id="community-outcome-title"
            className="mt-4 text-xl font-semibold text-primary-light-100"
          >
            Wynik nie jest dostępny
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-primary-light-300">
            Nie możemy potwierdzić tego wyniku. Powiadomienie może być nieaktualne albo nie należeć
            do tego konta.
          </p>
          <Link
            to="/app/community"
            className="rs-action-primary rs-action-md mt-6 w-full sm:w-auto"
          >
            Wróć do Społeczności
          </Link>
        </section>
      </OutcomeShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <OutcomeShell>
        <section className="rs-community-state p-5 sm:p-6" role="alert">
          <ExclamationTriangleIcon className="h-9 w-9 text-status-warning-400" aria-hidden="true" />
          <h1
            id="community-outcome-title"
            className="mt-4 text-xl font-semibold text-primary-light-100"
          >
            Nie udało się pobrać wyniku
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-primary-light-300">
            Wystąpił chwilowy problem. Spróbuj ponownie — powiadomienie nie zostało oznaczone jako
            odczytane.
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Button className="!min-h-11" onClick={() => setRetryVersion((version) => version + 1)}>
              Spróbuj ponownie
            </Button>
            <Link to="/app/community" className="rs-action-navigation rs-action-md justify-center">
              Wróć do Społeczności
            </Link>
          </div>
        </section>
      </OutcomeShell>
    );
  }

  const { outcome } = state;
  const isModerationOutcome = outcome.kind === 'community_moderation_action';
  const outcomeCopy = isModerationOutcome
    ? moderationCopy[outcome.moderationAction]
    : resolutionCopy[outcome.resolutionStatus];
  const occurredAtMillis = isModerationOutcome
    ? outcome.occurredAtMillis
    : outcome.resolvedAtMillis;
  return (
    <OutcomeShell label={isModerationOutcome ? 'Wynik moderacji' : 'Wynik zgłoszenia'}>
      <section className="rs-community-state p-5 sm:p-7" aria-live="polite">
        <CheckCircleIcon className="h-10 w-10 text-ratuj-300" aria-hidden="true" />
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ratuj-300">
          Wynik moderacji
        </p>
        <h1
          id="community-outcome-title"
          className="mt-2 text-2xl font-semibold text-primary-light-100"
        >
          {isModerationOutcome ? 'Aktualizacja moderacji' : 'Wynik zgłoszenia w Społeczności'}
        </h1>
        <p className="mt-4 max-w-xl text-base leading-7 text-primary-light-200">{outcomeCopy}</p>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-primary-light-400">Dotyczy</dt>
            <dd className="mt-1 font-medium text-primary-light-200">
              {outcome.subjectKind === 'post' ? 'Wpisu w Społeczności' : 'Wątku w Społeczności'}
            </dd>
          </div>
          <div>
            <dt className="text-primary-light-400">
              {isModerationOutcome ? 'Wystąpiło' : 'Rozstrzygnięto'}
            </dt>
            <dd className="mt-1 font-medium text-primary-light-200">
              <time dateTime={new Date(occurredAtMillis).toISOString()}>
                {formatResolvedAt(occurredAtMillis)}
              </time>
            </dd>
          </div>
        </dl>
        <p className="mt-5 text-sm leading-6 text-primary-light-300">
          {isModerationOutcome
            ? 'Nie musisz nic robić.'
            : 'Nie musisz podejmować żadnego działania.'}
        </p>
        <Link to="/app/community" className="rs-action-primary rs-action-md mt-6 w-full sm:w-auto">
          Wróć do Społeczności
        </Link>
      </section>
    </OutcomeShell>
  );
};

export default CommunityOutcomePage;
